"use client";
import Link from "next/link";
import React from "react";

import { blogSearchTypesenseClient } from "@/components/Blog/BlogSearch/client";
import Autocomplete from "@/components/ui/Autocomplete";
import type { AutocompleteProps } from "@/components/ui/Autocomplete/Autocomplete";
import { BLOG_INDEX_URL } from "@/data/helpers/blog/urls";
import type { BlogPost } from "@/types/search";
import { Paragraph as BaseParagraph, ParagraphProps } from "@cgc/ui/typography";
import { clsx } from "@cgc/ui/utils/clsx";

const Paragraph = ({ className, ...props }: ParagraphProps) => (
  <BaseParagraph
    size="p1"
    {...props}
    className={clsx("px-[15px] py-[7px]", className)}
  />
);

function Item({ item, className }: { item: BlogPost; className?: string }) {
  return (
    <Link href={item.url} className={className}>
      <Paragraph>{item.title}</Paragraph>
    </Link>
  );
}

type Props = AutocompleteProps & {
  refine?: (query) => void;
};

const BlogAutocomplete = ({ refine, ...props }: Props) => {
  const getSources = React.useMemo(
    () =>
      async ({ query }: { query: string }) => {
        const hitsLimit = 3;

        const blogPostSearch = await blogSearchTypesenseClient
          .collections<BlogPost>("blogPost")
          .documents()
          .search(
            {
              q: query,
              query_by: "title",
              highlight_full_fields: "title",
            },
            {},
          );
        const { found, hits } = blogPostSearch;
        const seeMoreCount = found - hitsLimit;

        return [
          {
            sourceId: "posts",
            getItems: () => (hits ?? []).slice(0, hitsLimit),
            templates: {
              header: ({ autocomplete }) => {
                if (found > 0) {
                  return (
                    <Paragraph className="text-cgc-green font-bold">
                      {found} article results
                    </Paragraph>
                  );
                } else {
                  return (
                    <Paragraph>
                      Sorry, there are no matches for <strong>‘{query}’</strong>
                      . Check out our <Link href={"/faqs"}>FAQ’s</Link> or the
                      rest of our{" "}
                      <Link
                        href={BLOG_INDEX_URL}
                        onClick={() => {
                          refine && refine("");
                          autocomplete.setIsOpen(false);
                        }}
                      >
                        blog
                      </Link>
                      .
                    </Paragraph>
                  );
                }
              },
              item: ({ item }) => <Item item={item.document} />,
              ...(found > 0
                ? {
                    footer: ({ autocomplete }) => (
                      <Paragraph>
                        <Link
                          href={`${BLOG_INDEX_URL}?q=${query}`}
                          onClick={() => {
                            refine && refine(query);
                            autocomplete.setIsOpen(false);
                          }}
                          className="italic underline"
                        >
                          {seeMoreCount > 0
                            ? `See ${seeMoreCount} more results...`
                            : "See all articles"}
                        </Link>
                      </Paragraph>
                    ),
                  }
                : null),
            },
          },
        ];
      },
    [refine],
  );

  return (
    <Autocomplete
      {...props}
      getSources={getSources}
      shouldPanelOpen={({ state }) => !!state.query}
    />
  );
};

export default BlogAutocomplete;
